import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../../../../components/recharts/functions";
import { ReMixChart } from "../../../../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../../../../models/chart_model";
import { renderHourTick } from "../../../../../../components/recharts/components";
import { R_CHART_TYPES } from "../../../../const";
import { fetchOptimalProcurementData } from "../../../../../../Rest_api/DemoRestApi";
import loader from "../../../../../../components/recharts/loader.gif";
import { getMonthFromDate } from "../../../../../../extensions/date";

const DataTabs = ["Slot", "Date", "Month"];

const SelectionOptions = [
  { id: "surplus", label: "Surplus", color: "#20B2AA" },
  { id: "deficit", label: "Deficit", color: "#F1935C" },
];

interface SummaryData {
  date: string;
  slot: number;
  deficit?: number;
  surplus?: number;
  net_deficit?: number;
  prev_deficit?: number;
  prev_surplus?: number;
  total_surplus?: number;
  total_offered?: number;
  market_buy?: number;
  market_purchase?: number;
  procurement_cost?: number;
  marketprice?: number;
  [key: string]: any;
}

const Intervals = [
  {
    label: "Time Slot",
    key: "time",
    values: ["slot", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const OFFER_COLORS = [
  "#4169E1",
  "#6495ED",
  "#00BFFF",
  "#1E90FF",
  "#5F9EA0",
  "#4682B4",
  "#87CEEB",
  "#6A5ACD",
];

function SurplusSelector({
  selected,
  onSelectionChange,
}: {
  selected: string[];
  onSelectionChange: (selected: string[]) => void;
}) {
  const toggleSelection = (id: string) => {
    if (selected.includes(id)) {
      if (selected.length > 1) {
        onSelectionChange(selected.filter((s) => s !== id));
      } else {
        onSelectionChange(SelectionOptions.map((opt) => opt.id));
      }
    } else {
      onSelectionChange([...selected, id]);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {SelectionOptions.map((option) => {
        const isSelected = selected.includes(option.id);
        return (
          <button
            key={option.id}
            onClick={() => toggleSelection(option.id)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all duration-200 ${
              isSelected
                ? "bg-white shadow-sm border border-gray-200"
                : "bg-transparent hover:bg-gray-100"
            }`}
          >
            <div
              className={`w-3 h-3 rounded transition-all duration-200 ${
                isSelected ? "opacity-100" : "opacity-40"
              }`}
              style={{ backgroundColor: option.color }}
            />
            <span
              className={`font-medium text-sm transition-all duration-200 ${
                isSelected ? "text-gray-700" : "text-gray-500"
              }`}
            >
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function OptimalVolumeAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  setUniqueTargetDate,
  selectedTargetDate,
}: {
  selectedBlock: any[];
  refreshKey: number;
  setError?: any;
  setUniqueTargetDate?: any;
  selectedTargetDate: any[];
}) {
  const [data, setData] = useState<SummaryData[]>([]);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<string>(DataTabs[0]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([
    "surplus",
    "deficit",
  ]);
  const [allOfferFields, setAllOfferFields] = useState<string[]>([]);
  const [selectedChartType] = useState(R_CHART_TYPES[1]);

  useEffect(() => {
    const tabIndex = DataTabs.indexOf(activeTab);
    if (tabIndex !== -1) {
      setSelectedInterval(Intervals[tabIndex]);
    }
  }, [activeTab]);

  const detectAllOfferFields = useCallback((dataArray: any[]) => {
    if (!dataArray || dataArray.length === 0) return [];

    const offerSet = new Set<number>();

    dataArray.forEach((item) => {
      Object.keys(item).forEach((key) => {
        const match = key.match(/^offer(\d+)$/);
        if (match) {
          offerSet.add(parseInt(match[1]));
        }
      });
    });

    const sortedOffers = Array.from(offerSet)
      .sort((a, b) => a - b)
      .map((num) => `offer${num}`);

    return sortedOffers;
  }, []);

  const handleFetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchOptimalProcurementData();
      let dataArray = Array.isArray(res) ? res : res?.data || [];

      if (!Array.isArray(dataArray)) {
        console.error("Data is not an array:", dataArray);
        setError?.("Invalid data format: expected array.");
        setAllOfferFields([]);
        setData([]);
        return;
      }

      const detectedOffers = detectAllOfferFields(dataArray);
      setAllOfferFields(detectedOffers);

      if (setUniqueTargetDate && dataArray.length > 0) {
        const uniqueDates = Array.from(
          new Set(
            dataArray
              .filter((item) => item.date)
              .map((item: { date: string }) =>
                item.date.split("-").reverse().join("-")
              )
          )
        );
        setUniqueTargetDate(uniqueDates);
      }

      setData(dataArray);
    } catch (error) {
      console.error("Error fetching data", error);
      setError?.("An error occurred while fetching data.");
      setAllOfferFields([]);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [setError, setUniqueTargetDate, detectAllOfferFields]);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData, refreshKey]);

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.key === "time") {
      return "slot";
    }
    return selectedInterval.key;
  }, [selectedInterval]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  useEffect(() => {
    if (!data || data.length === 0) {
      setChartData([]);
      setChartKeys([]);
      return;
    }

    let filteredData = data.map((item) => {
      const cleanedItem: any = {
        ...item,
        month: getMonthFromDate(item.date),
      };

      // Replace NaN values with 0 for all numeric fields
      Object.keys(cleanedItem).forEach((key) => {
        const value = cleanedItem[key];
        if (typeof value === "number" && isNaN(value)) {
          cleanedItem[key] = 0;
        } else if (
          typeof value === "string" &&
          (value === "NaN" || value.toLowerCase() === "nan")
        ) {
          cleanedItem[key] = 0;
        } else if (value === null || value === undefined) {
          // Convert to 0 for numeric fields
          if (
            key.startsWith("offer") ||
            key.startsWith("cost") ||
            key.includes("deficit") ||
            key.includes("surplus") ||
            key.includes("market") ||
            key.includes("procurement")
          ) {
            cleanedItem[key] = 0;
          }
        }
      });

      return cleanedItem;
    });

    if (selectedTargetDate && selectedTargetDate.length > 0) {
      const types = selectedTargetDate.map(
        (t: any) => t.value?.split("-").reverse().join("-") ?? t
      );
      filteredData = filteredData.filter((row) => types.includes(row.date));
    }

    function getKeys() {
      if (selectedInterval.key === "time") {
        return ["slot", "date"];
      } else if (selectedInterval.key === "date") {
        return ["date"];
      }
      return [selectedInterval.values[0]];
    }

    // Only include the specific fields shown in the chart image
    const aggregationKeys = [
      "prev_deficit",
      "market_buy",
      "total_surplus",
      "prev_surplus",
    ];

    // Add only the offer fields (not cost fields)
    allOfferFields.forEach((offer) => {
      aggregationKeys.push(offer);
    });

    const par: ChartDataParams = {
      keys: getKeys(),
      tableKeys: ["slot", "date", "month"],
      groupBy: [""],
      volumeKey: "",
      aggregationKeys: aggregationKeys,
      averageKeys: [],
      averageDuplicationKeys: [],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);

    // Additional cleaning after aggregation - Remove zero values completely
    const cleanedChartData = cData.data.map((item: any) => {
      const cleaned = { ...item };
      Object.keys(cleaned).forEach((key) => {
        // Skip non-numeric keys (slot, date, month, etc.)
        if (key === 'slot' || key === 'date' || key === 'month') {
          return;
        }
        
        const value = cleaned[key];
        if (typeof value === "number" && isNaN(value)) {
          cleaned[key] = undefined;
        } else if (
          typeof value === "string" &&
          (value === "NaN" || value.toLowerCase() === "nan")
        ) {
          cleaned[key] = undefined;
        } else if (typeof value === "number" && value === 0) {
          // Set zero values to undefined so they don't render in the chart
          cleaned[key] = undefined;
        } else if (value === null) {
          cleaned[key] = undefined;
        }
      });
      return cleaned;
    });

    setChartData(cleanedChartData);
    setChartKeys(cData.keys);
  }, [
    data,
    selectedBlock,
    selectedInterval,
    selectedTargetDate,
    allOfferFields,
  ]);

  const activeOfferFields = useMemo(() => {
    if (
      !chartData ||
      chartData.length === 0 ||
      !allOfferFields ||
      allOfferFields.length === 0
    ) {
      return [];
    }

    const activeOffers: string[] = [];

    allOfferFields.forEach((offerField) => {
      const hasData = chartData.some((item) => {
        const offerKeys = Object.keys(item).filter(
          (key) => key === offerField || key.endsWith(`_${offerField}`)
        );

        return offerKeys.some((key) => {
          const value = item[key];
          const numValue =
            typeof value === "string" ? parseFloat(value) : value;
          return (
            numValue !== null &&
            numValue !== undefined &&
            numValue > 0 &&
            !isNaN(numValue) &&
            isFinite(numValue)
          );
        });
      });

      if (hasData) {
        activeOffers.push(offerField);
      }
    });

    return activeOffers;
  }, [chartData, allOfferFields]);

  // Helper function to check if a specific dataKey has any non-zero/non-null values
  const hasNonZeroValuesForDataKey = useCallback(
    (dataKey: string) => {
      if (!chartData || chartData.length === 0) return false;

      return chartData.some((item) => {
        const value = item[dataKey];
        if (value === null || value === undefined) return false;
        
        const numValue =
          typeof value === "string" ? parseFloat(value) : value;
        return (
          numValue !== 0 &&
          !isNaN(numValue) &&
          isFinite(numValue) &&
          numValue > 0
        );
      });
    },
    [chartData]
  );

  const chartSuffixes = useMemo(() => {
    const metrics = [];

    // Add offer fields (blue colors)
    if (activeOfferFields.length > 0) {
      activeOfferFields.forEach((offerField, index) => {
        const offerNum = parseInt(offerField.replace("offer", ""));
        metrics.push({
          suffix: offerField,
          displayName: `Offer ${offerNum} Quantum`,
          stackSuffix: "stack1",
          stroke: OFFER_COLORS[index % OFFER_COLORS.length],
          fill: OFFER_COLORS[index % OFFER_COLORS.length],
          chartType: ChartType.Bar,
          unit: "MW",
          yAxisId: "left",
        });
      });
    }

    // Add market_buy stacked on offers - only if deficit is selected
    if (selectedOptions.includes("deficit")) {
      metrics.push({
        suffix: "market_buy",
        displayName: "Market Buy",
        stackSuffix: "stack1",
        stroke: "#FF4747",
        fill: "#FF4747",
        chartType: ChartType.Bar,
        unit: "MW",
        yAxisId: "left",
      });
    }

    // Add prev_surplus stacked on offers - only if surplus is selected
    if (selectedOptions.includes("surplus")) {
      metrics.push({
        suffix: "prev_surplus",
        displayName: "Prev Surplus",
        stackSuffix: "stack1",
        stroke: "#00BF6F",
        fill: "#00BF6F",
        chartType: ChartType.Bar,
        unit: "MW",
        yAxisId: "left",
      });
    }

    // Add prev_deficit - only if deficit is selected
    if (selectedOptions.includes("deficit")) {
      metrics.push({
        suffix: "prev_deficit",
        displayName: "Prev Deficit",
        stackSuffix: "stack2",
        stroke: "#F1935C",
        fill: "#F1935C",
        chartType: ChartType.Bar,
        unit: "MW",
        yAxisId: "left",
      });
    }

    // Add total_surplus (Net Surplus) - only if surplus is selected
    if (selectedOptions.includes("surplus")) {
      metrics.push({
        suffix: "total_surplus",
        displayName: "Net Surplus",
        stackSuffix: "stack2",
        stroke: "#34656D",
        fill: "#34656D",
        chartType: ChartType.Bar,
        unit: "MW",
        yAxisId: "left",
      });
    }
    return metrics;
  }, [selectedOptions, activeOfferFields]);

  const createChartObject = useCallback(
    (
      key: string,
      suffix: string,
      displayName: string,
      stackSuffix: string,
      chartType: ChartType,
      unit: string,
      stroke: string,
      fill: string,
      yAxisId: string
    ) => {
      const dataKey = key ? `${key}_${suffix}` : suffix;
      return {
        dataKey: dataKey,
        name: displayName,
        legendColor: fill === "none" ? stroke : fill,
        type: chartType,
        unit: unit,
        stroke: stroke,
        fill: fill,
        yAxisId: yAxisId,
        stackId: stackSuffix || undefined,
      };
    },
    []
  );

  const legends = useMemo(() => {
    if (!chartKeys || chartKeys.length === 0 || chartSuffixes.length === 0) {
      return [];
    }

    const result: any[] = [];

    chartSuffixes.forEach(
      ({
        suffix,
        displayName,
        stackSuffix,
        chartType,
        unit,
        yAxisId,
        stroke,
        fill,
      }) => {
        chartKeys.forEach((key) => {
          const chartObj = createChartObject(
            key,
            suffix,
            displayName,
            stackSuffix,
            chartType,
            unit,
            stroke,
            fill || stroke,
            yAxisId
          );

          // Only add to legends if the dataKey has non-zero values
          if (hasNonZeroValuesForDataKey(chartObj.dataKey)) {
            result.push(chartObj);
          }
        });
      }
    );

    return result;
  }, [chartKeys, chartSuffixes, createChartObject, hasNonZeroValuesForDataKey]);

  function secondDataKey() {
    if (selectedInterval.isTime && selectedBlock.length > 0) {
      return "date";
    }
    if (selectedInterval.isTime) {
      return "date";
    }
    return undefined;
  }

  return (
    <div className="dash-card !border !border-[#DFEAF2] bg-white  w-full !min-h-[100%] rounded-2xl p-4">
      <div className="flex justify-between items-center border-b border-[#DFEAF2] pb-3 mb-4">
        <div className="flex items-center gap-3">
          <h2 className="chartHeading !text-[#34656D]">
            System Balance (Surplus/Deficit)
          </h2>
          {activeOfferFields.length > 0 && (
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
              {activeOfferFields.length} Active Offer
              {activeOfferFields.length > 1 ? "s" : ""}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <SurplusSelector
            selected={selectedOptions}
            onSelectionChange={setSelectedOptions}
          />
        </div>
      </div>

      {loading ? (
        <div className="w-full bg-white flex items-center justify-center min-h-[400px]">
          <img src={loader} width={200} alt="loader" className="block" />
        </div>
      ) : chartData.length === 0 ? (
        <div className="w-full bg-white flex items-center justify-center min-h-[400px]">
          <p className="text-gray-500">No data available</p>
        </div>
      ) : (
        <ReMixChart
          chartHeight="calc(100% - 70px)"
          data={chartData}
          showBrush={chartData.length > 96 ? true : undefined}
          brushStart={BrushStart.Start}
          isTimeSlot={selectedInterval.isTime}
          onlyTitle={true}
          legends={legends}
          yAxisLabel="MW"
          nameGap={40}
          xDataKey={xDataKey}
          top={10}
          bottom={30}
          xTick={
            selectedInterval.isTime && selectedBlock.length === 0
              ? (value: any, index: any) => renderHourTick(value, 10)
              : undefined
          }
          secondXDataKey={secondDataKey()}
          showToolbox={false}
        />
      )}
    </div>
  );
}